import React, { useContext, useEffect, useState } from 'react';
import HeaderDesktop from 'src/components/layout/redesign/header-desktop';
import { amplitudeTrackEvent } from 'src/helpers/amplitude-event-track';
import PromoRibbon from 'src/components/layout/redesign/header-shared/promo-ribbon';
import RecentlyViewedClubRibbon from 'src/components/layout/redesign/header-shared/recently-viewed-club-ribbon';
import { GlobalCmsContent } from 'src/content/global-content';
import { SiteContext } from 'src/context/site-context';
import { getLocalStorageItem } from 'src/helpers/local-storage-helper';
import {
  MOST_RECENTLY_VIEW_CLUB_ID,
  MOST_RECENTLY_VIEW_SEARCH_CLUB_ID,
} from 'src/components/layout/redesign/header-shared/promo-helper';
import { RedesignButtonStyle } from 'src/enums/button-style';
import HeaderDesktopMenu from 'src/components/layout/redesign/header-desktop/header-menu';
import { CLUBS_LINK } from 'src/enums/clubs';
import ClubSearch from 'src/components/layout/redesign/global-header-desktop/club-search';
import HeaderUnderlay from 'src/components/layout/redesign/global-header-desktop/header-underlay';
import useWindowSize from 'src/custom-hooks/use-window-size';
import type { GlobalHeaderContent } from 'src/content/cms-provider/global-cms-provider';
import { RecentlyVisitedClubInfo } from 'src/components/common/recently-visited-club-info';
import { calculateDesktopGridWidth } from 'src/styling/constants';

const getShouldShowClubRibbon = (headerContent: GlobalHeaderContent) => {
  if (!headerContent?.headerRecentlyViewedClubRibbon?.enabled) {
    return false;
  }

  return !!getLocalStorageItem(MOST_RECENTLY_VIEW_CLUB_ID);
};

const GlobalHeaderDesktop = () => {
  const [isClubSearchOpen, setIsClubSearchOpen] = useState(false);

  const { headerContent } = useContext(GlobalCmsContent);
  const { pageLevel } = useContext(SiteContext);
  const { isMobileHeader } = useWindowSize();

  const [shouldShowClubRibbon, setShouldShowClubRibbon] = useState<boolean>(
    getShouldShowClubRibbon(headerContent)
  );
  const [isRecentRibbonVisible, setIsRecentRibbonVisible] = useState<boolean>(true);

  useEffect(() => {
    setShouldShowClubRibbon(getShouldShowClubRibbon(headerContent));
  }, [headerContent, isMobileHeader]);

  const showRecentlyVisitedClubInfo = !!getLocalStorageItem(MOST_RECENTLY_VIEW_SEARCH_CLUB_ID);

  return (
    <HeaderDesktop
      headerContent={headerContent}
      primaryButton={
        headerContent?.headerButton
          ? {
              type: 'link',
              buttonProps: headerContent.headerButton,
              handleClick: () => {
                amplitudeTrackEvent('Click to Join Club', {
                  custom_page: window.location.origin,
                  custom_level: pageLevel,
                });
              },
            }
          : undefined
      }
      secondaryButton={
        headerContent?.searchSettings && {
          type: 'button',
          buttonStyle: RedesignButtonStyle.WHITE_WITH_GREEN_FONT,
          handleClick: () => {
            amplitudeTrackEvent('Click to "Find a club" - Navigation', {
              custom_page: window.location.origin,
              custom_level: pageLevel,
            });
            setIsClubSearchOpen(true);
            setIsRecentRibbonVisible(false);
          },
          text: headerContent.searchSettings.openSearchButtonName,
        }
      }
      onMenuChanged={() => {
        setIsRecentRibbonVisible(true);
        setIsClubSearchOpen(false);
      }}
      additionalSubMenus={
        headerContent?.searchSettings && (
          <>
            <HeaderDesktopMenu
              description={headerContent.searchSettings.description}
              name={headerContent.searchSettings.title}
              visible={isClubSearchOpen}
              levelTwoMenuItems={[]}
              link={CLUBS_LINK}
              overviewText={headerContent.searchSettings.linkToMapText}
              enableOverflow
              onFinish={() => {
                setIsRecentRibbonVisible(true);
                setIsClubSearchOpen(false);
              }}
            >
              <div css={{ width: calculateDesktopGridWidth(10, 6) }}>
                <ClubSearch
                  onFinish={() => {
                    setIsClubSearchOpen(false);
                    setIsRecentRibbonVisible(true);
                  }}
                />
                {showRecentlyVisitedClubInfo && <RecentlyVisitedClubInfo />}
              </div>
            </HeaderDesktopMenu>
            {!isMobileHeader && isClubSearchOpen && (
              <HeaderUnderlay
                tabIndex={0}
                onClick={() => setIsClubSearchOpen(false)}
                onFocus={() => setIsClubSearchOpen(false)}
              />
            )}
          </>
        )
      }
      ribbonChildren={
        <>
          {headerContent?.headerPromoRibbon && (
            <PromoRibbon
              isCollapsed={shouldShowClubRibbon}
              promoRibbon={headerContent.headerPromoRibbon}
            />
          )}
          <RecentlyViewedClubRibbon
            enabled={shouldShowClubRibbon && isRecentRibbonVisible}
            setShouldShowClubRibbon={setShouldShowClubRibbon}
          />
        </>
      }
    />
  );
};

export default GlobalHeaderDesktop;
